import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Building2,
  Save,
  ArrowLeft,
  Loader2,
  FileText,
  Globe,
  Tag
} from 'lucide-react'
import { brandsService } from '@/services/brands'
import { ROUTES } from '@/utils/constants'
import { NavigationUtils } from '@/utils/navigation'
import toast from 'react-hot-toast'

const BrandEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  // Fetch brand data
  const {
    data: brand,
    isLoading: brandLoading,
    error: brandError
  } = useQuery(
    ['brand', id],
    () => brandsService.getById(id),
    {
      onError: (error) => {
        toast.error('Failed to load brand details')
        console.error('Error loading brand:', error)
      }
    }
  )

  // Populate form when data is available (from cache or server)
  useEffect(() => {
    if (brand) {
      setValue('name', brand.name)
      setValue('description', brand.description || '')
      setValue('is_active', brand.is_active)
    }
  }, [brand, setValue])

  // Update brand mutation
  const updateBrandMutation = useMutation(
    (brandData) => brandsService.update(id, brandData),
    {
      onSuccess: () => {
        toast.success('Brand updated successfully!')
        // Invalidate all brand-related queries for real-time updates
        queryClient.invalidateQueries(['brand', id])
        queryClient.invalidateQueries(['brands'])
        queryClient.invalidateQueries(['brands', 'all'])
        queryClient.invalidateQueries('brands-for-user')
        queryClient.invalidateQueries('brands-for-user-edit')
        navigate(`/brands/${id}`) // Navigate to brand detail page
      },
      onError: (error) => {
        toast.error('Failed to update brand')
        console.error('Error updating brand:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    updateBrandMutation.mutate(data)
  }

  if (brandLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading brand details...</span>
        </div>
      </div>
    )
  }

  if (brandError || !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <Building2 className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Brand not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The brand you're trying to edit doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'brands')}
              className="btn-primary btn-sm"
            >
              Back to Brands
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/brands/${id}`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Brand
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Brand: {brand.name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Update brand information and settings
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Brand Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update brand details and configuration
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Brand Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Brand Name *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('name', {
                    required: 'Brand name is required',
                    minLength: {
                      value: 2,
                      message: 'Brand name must be at least 2 characters',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter brand name"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="input pl-10 resize-none"
                  placeholder="Enter brand description"
                />
              </div>
            </div>



            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  {...register('is_active')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active Brand</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Inactive brands are hidden from users and cannot be used
              </p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/brands/${id}`)}
            className="btn-outline btn-md"
            disabled={updateBrandMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={updateBrandMutation.isLoading}
          >
            {updateBrandMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Brand
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default BrandEdit
